<template>
  <div class="border shadow-2xl rounded-3xl bg-black/60 backdrop-blur-lg border-white/20 p-6 transform transition-all duration-300 hover:scale-[1.02] card-hover">
    <!-- Chart Title Section -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-gradient-to-t from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-bold text-white">{{ activityTitle }}</h3>
          <p class="text-sm text-gray-300">{{ entriesLabel }}</p>
        </div>
      </div>
      <div class="flex items-center space-x-2">
        <div class="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
        <span class="text-sm font-medium text-gray-300">Live Data</span>
      </div>
    </div>
    
    <!-- Chart Canvas -->
    <div class="h-80">
      <canvas ref="chart"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart, LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from 'chart.js';

Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

export default {
  props: {
    chartData: {
      type: Array,
      required: true
    },
    dateRange: {
      type: Object,
      default: () => ({})
    },
    currentPreset: {
      type: String,
      default: 'last30'
    }
  },
  data() {
    return {
      myChart: null,
      darkMode: true,
    };
  },
  computed: {
    activityTitle() {
      // Calculate the difference in days between start and end dates
      const start = new Date(this.dateRange.startDate);
      const end = new Date(this.dateRange.endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return "Activity";
      }
      
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Determine appropriate title based on date range
      if (diffDays <= 1) {
        return "Daily Activity";
      } else if (diffDays <= 7) {
        return "Weekly Activity";
      } else if (diffDays <= 31) {
        return "Monthly Activity";
      } else if (diffDays <= 90) {
        return "Quarterly Activity";
      } else if (diffDays <= 365) {
        return "Annual Activity";
      } else {
        return "Historical Activity";
      }
    },
    entriesLabel() {
      // Show monthly label for all time and last 12 months
      if (this.currentPreset === 'all' || this.currentPreset === 'last12Months') {
        return 'Entries per month';
      }
      return 'Entries per day';
    },
    shouldGroupByMonth() {
      // Group by month for "all" and "last12Months" presets
      return this.currentPreset === 'all' || this.currentPreset === 'last12Months';
    }
  },
  mounted() {
    this.renderChart();
  },
  watch: {
    chartData() {
      this.updateChartData();
    },
    dateRange: {
      handler() {
        this.updateChartData();
      },
      deep: true
    },
    currentPreset() {
      this.updateChartData();
    }
  },
  methods: {
    formatDate(inputDate) {
      const date = new Date(inputDate);

      if (isNaN(date.getTime())) {
        // Instead of returning 'Today', return the original input or a fallback
        return inputDate || 'Unknown Date';
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // +1 because months are 0 indexed
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    },
    formatMonthLabel(year, month) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month]} ${year}`;
    },
    renderChart() {
      const data = this.structureData(this.chartData);

      const ctx = this.$refs.chart.getContext('2d');
      // Enhanced gradient for dark mode compatibility
      const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
      if (this.darkMode) {
        gradientStroke.addColorStop(1, 'rgba(59, 130, 246, 0.3)'); // Blue with opacity
        gradientStroke.addColorStop(0.5, 'rgba(59, 130, 246, 0.1)');
        gradientStroke.addColorStop(0, 'rgba(59, 130, 246, 0.0)');
      } else {
        gradientStroke.addColorStop(1, 'rgba(59, 130, 246, 0.2)');
        gradientStroke.addColorStop(0.5, 'rgba(59, 130, 246, 0.05)');
        gradientStroke.addColorStop(0, 'rgba(59, 130, 246, 0.0)');
      }

      data.datasets[0].backgroundColor = gradientStroke;
      data.datasets[0].borderColor = '#3b82f6'; // Modern blue
      data.datasets[0].borderWidth = 3;
      data.datasets[0].pointBackgroundColor = this.darkMode ? '#1f2937' : '#ffffff';
      data.datasets[0].pointBorderColor = '#3b82f6';
      data.datasets[0].pointBorderWidth = 2;
      data.datasets[0].pointRadius = 6;
      data.datasets[0].pointHoverRadius = 8;
      data.datasets[0].tension = 0.4; // Smooth curves

      if (this.myChart) {
        this.myChart.destroy();
      }

      this.myChart = new Chart(ctx, {
        type: 'line',
        data: data,
        options: {
          maintainAspectRatio: false,
          responsive: true,
          interaction: {
            intersect: false,
            mode: 'index',
          },
          plugins: {
            tooltip: {
              enabled: true,
              backgroundColor: this.darkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
              titleColor: this.darkMode ? '#f3f4f6' : '#1f2937',
              bodyColor: this.darkMode ? '#d1d5db' : '#374151',
              borderColor: this.darkMode ? '#4b5563' : '#e5e7eb',
              borderWidth: 1,
              cornerRadius: 8,
              displayColors: false,
              callbacks: {
                title: (tooltipItems) => {
                  // The label is already formatted by structureData method
                  return tooltipItems[0].label;
                },
                label: (tooltipItem) => {
                  return `Entries: ${tooltipItem.parsed.y}`;
                }
              }
            },
            legend: {
              display: false
            }
          },


          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              ticks: {
                padding: 12,
                color: this.darkMode ? "#9ca3af" : "#6b7280",
                font: {
                  size: 11,
                  weight: '500'
                }
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: this.darkMode ? 'rgba(156, 163, 175, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                drawBorder: false,
                borderDash: [5, 5]
              },
              ticks: {
                padding: 12,
                color: this.darkMode ? "#9ca3af" : "#6b7280",
                font: {
                  size: 11,
                  weight: '500'
                }
              }
            }
          }
        }
      });
    },
    updateChartData() {
      if (this.myChart) {
        this.myChart.destroy(); // Destroy the old chart
      }
      this.renderChart(); // Re-render the chart
    },
    structureData(entries) {
      if (this.shouldGroupByMonth) {
        return this.structureDataByMonth(entries);
      }
      
      // Extract dates from entries and format them properly
      let datesWithCounts = {};
      
      // Process each entry
      entries.forEach(entry => {
        // Get just the date part from the datetime string
        const datePart = entry.date_created.split(' ')[0];
        // Format the date for display
        const formattedDate = this.formatDate(datePart);
        
        // Count entries per date
        datesWithCounts[formattedDate] = (datesWithCounts[formattedDate] || 0) + 1;
      });
      
      // Sort dates chronologically
      const sortedDates = Object.keys(datesWithCounts).sort((a, b) => {
        // Convert DD-MM-YYYY to Date objects for comparison
        const [dayA, monthA, yearA] = a.split('-');
        const [dayB, monthB, yearB] = b.split('-');
        return new Date(yearA, monthA-1, dayA) - new Date(yearB, monthB-1, dayB);
      });
      
      return {
        labels: sortedDates,
        datasets: [{
          label: ' Entries per day',
          data: sortedDates.map(date => datesWithCounts[date]),
          borderWidth: 5
        }]
      };
    },
    structureDataByMonth(entries) {
      // Group entries by month-year
      let monthsWithCounts = {};
      
      // Process each entry
      entries.forEach(entry => {
        // Get just the date part from the datetime string
        const datePart = entry.date_created.split(' ')[0];
        const date = new Date(datePart);
        
        if (isNaN(date.getTime())) {
          return; // Skip invalid dates
        }
        
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-11
        
        // Create a key for month-year
        const monthKey = `${year}-${month}`;
        
        // Count entries per month
        monthsWithCounts[monthKey] = (monthsWithCounts[monthKey] || 0) + 1;
      });
      
      // Convert to array and sort chronologically
      const sortedMonths = Object.keys(monthsWithCounts).sort((a, b) => {
        const [yearA, monthA] = a.split('-').map(Number);
        const [yearB, monthB] = b.split('-').map(Number);
        
        if (yearA !== yearB) {
          return yearA - yearB;
        }
        return monthA - monthB;
      });
      
      // Format labels and get counts
      const labels = sortedMonths.map(key => {
        const [year, month] = key.split('-').map(Number);
        return this.formatMonthLabel(year, month);
      });
      
      const data = sortedMonths.map(key => monthsWithCounts[key]);
      
      return {
        labels: labels,
        datasets: [{
          label: ' Entries per month',
          data: data,
          borderWidth: 5
        }]
      };
    }
  }
};
</script>
